import {
  autoinject,
  ComponentAttached,
  observable,
  computedFrom,
} from "aurelia-framework";
import { HttpResponseMessage } from "aurelia-http-client";
import { I18N } from "aurelia-i18n";
import { RoutableComponentCanDeactivate, Router } from "aurelia-router";
import { EventAggregator } from "aurelia-event-aggregator";
import { ProductfinderData, Pumpentyp } from "./productfinder-data";
import { ProductfinderService } from "./productfinder-service";
import { ApplicationState } from "../../services/application-state";
import { LocaleService } from "../../services/locale-service";
import { DialogFactory } from "../../services/dialog-factory";
import {
  DownloadType,
  MatrixData,
  ProductfinderDownloadData,
} from "./productfinder-downloads-data";
import { saveAs } from "file-saver";
import { ProductfinderResult } from "./productfinder-result";
import { SelectItem } from "../../components/select-item";

@autoinject
export class ProductfinderForm
  implements ComponentAttached, RoutableComponentCanDeactivate
{
  inputData: ProductfinderData;
  resultData: ProductfinderResult;
  downloadData: ProductfinderDownloadData;
  @observable pumpentyp: Pumpentyp;
  matrixData: MatrixData;

  message: string;

  form: HTMLFormElement;

  constructor(
    private i18n: I18N,
    private router: Router,
    private service: ProductfinderService,
    private localeService: LocaleService,
    private state: ApplicationState,
    private dialogFactory: DialogFactory,
    private eventAggregator: EventAggregator,
  ) {
    this.loadDataFromState(state);

    // this dialog wants to be notified about locale changes
    eventAggregator.subscribe("i18n:locale:changed", () =>
      this.localeChanged(),
    );
  }

  private loadDataFromState(state: ApplicationState) {
    try {
      this.downloadData =
        this.state.productfinderDownloadData || new ProductfinderDownloadData();
    } catch (e) {
      console.error("could not load state from storage", e);
      this.downloadData = new ProductfinderDownloadData();
    }
    try {
      this.inputData = this.state.productfinderData || new ProductfinderData();
    } catch (e) {
      console.error("could not load state from storage", e);
      this.inputData = new ProductfinderData();
    }
    try {
      this.resultData =
        this.state.productfinderResult || new ProductfinderResult();
    } catch (e) {
      console.error("could not load state from storage", e);
      this.resultData = new ProductfinderResult();
    }
  }

  /**
   * Clear the form when the locale changes. This is neccessary unfortunately
   * because the input elements for the matrix data get translated on the server.
   * Therefore we take the simple approach of just clearing the form.
   */
  private localeChanged() {
    this.downloadData.clear();
    this.state.productfinderDownloadData = this.downloadData;
  }

  attached(): void {
    // Save the application state each time any element in the form is changed!
    // Blur event does not work, because the event gets fired, before Aurelia change the model, so each time the old state is saved
    this.form.addEventListener(
      "change",
      (ev: Event) => {
        let target = ev.target as HTMLInputElement;
        if (target.id) {
          this.downloadData[target.id] = new SelectItem(
            target.value,
            target.textContent || "",
            true,
          );
          this.state.productfinderDownloadData = this.downloadData;
        }
      },
      /*useCapture=*/ true,
    );
  }

  activate(params, routeConfig, navigationInstruction) {
    // Preset pumpentyp selection from storage see: Ausbaustufe Februar 2020 Kapitel 7
    if (
      params &&
      params.index &&
      (params.index === "0" || params.index === "1") &&
      this.resultData.pumpen &&
      this.resultData.pumpen[params.index].pumpentyp
    ) {
      // set id to "-" because the output page displays objects from type 'Pumpe' whereas this site objects from type 'Pumpentyp'
      this.reset_(false);
      this.pumpentyp = new Pumpentyp(
        "-",
        this.resultData.pumpen[params.index].pumpentyp.text,
        this.resultData.pumpen[0].pumpentyp.selected,
      );
    } else if (this.inputData.pumpentyp) {
      this.reset_(false);
      this.pumpentyp = this.inputData.pumpentyp;
    } else if (this.downloadData.pumpentyp) {
      this.pumpentyp = this.downloadData.pumpentyp;
    }
  }

  canDeactivate(): boolean {
    /*
         if (!this.isAllowedToLeave()) {
         return confirm(this.i18n.tr('reallyleave'));
         }
         */
    return true;
  }

  pumpentypChanged(newValue: Pumpentyp, oldValue: Pumpentyp): void {
    this.downloadData.pumpentyp = newValue;

    if (newValue) {
      this.getMatrixdata(this.downloadData.pumpentyp.text)
        .then((response) => {
          this.matrixData = response;

          // set default values. they are the first entries in the handed lists
          // if (this.matrixData.drehrichtungen.length == 1)
          this.downloadData.drehrichtung = this.matrixData.drehrichtungen[0];

          // if (this.matrixData.befestigungsarten.length == 1)
          this.downloadData.befestigungsart =
            this.matrixData.befestigungsarten[0];

          // if (this.matrixData.fluessigkeitsvorlagen.length == 1)
          this.downloadData.fluessigkeitsvorlage =
            this.matrixData.fluessigkeitsvorlagen[0];

          // if (this.matrixData.gehaeuseanschluesse.length == 1)
          this.downloadData.gehaeuseanschluss =
            this.matrixData.gehaeuseanschluesse[0];

          // if (this.matrixData.ventilanbauten.length == 1)
          this.downloadData.ventilanbau = this.matrixData.ventilanbauten[0];

          // if (this.matrixData.gehaeusewerkstoffe.length == 1)
          this.downloadData.gehaeusewerkstoff =
            this.matrixData.gehaeusewerkstoffe[0];
        })
        .catch((e) => {
          this.matrixData = new MatrixData();
          console.log("ERROR", e);
        });
    } else {
      this.reset_(false);
    }
  }

  getPumpentypen(search: string): Promise<Pumpentyp[]> {
    return this.service.getPumpentypen(search, null);
  }

  getMatrixdata(pumpentyp: string): Promise<MatrixData> {
    return this.service.getMatrixdata(pumpentyp);
  }

  downloadPDF(): void {
    this.download(DownloadType.PDF);
  }

  submit(): void {
    this.download(DownloadType.STEP);
  }

  download(type: DownloadType): void {
    this.message = "";
    let filename: string;
    this.service
      .getDownloadFilename(this.downloadData, type)
      .then((response) => {
        if (response.filename && !response.error) {
          filename = response.filename;

          this.service
            .download(this.downloadData, type)
            .then((response) => {
              const blob = new Blob([response.response], {
                type: "application/octet-stream",
              });
              saveAs(blob, filename || this.generateFileName(type));
            })
            .catch((e: HttpResponseMessage) => {
              console.log("ERROR", e);
            });
        } else {
          this.message = response.error;
        }
      })
      .catch((e) => {
        console.log("ERROR", e);
      });
  }

  generateFileName(type: DownloadType): string {
    return (
      this.downloadData.pumpentyp.text.replace(" ", "") +
      "_" +
      this.downloadData.drehrichtung.id +
      "_" +
      this.downloadData.befestigungsart.id +
      "_" +
      this.downloadData.fluessigkeitsvorlage.id +
      "_" +
      this.downloadData.gehaeuseanschluss.id +
      "_" +
      this.downloadData.ventilanbau.id +
      "_" +
      this.downloadData.gehaeusewerkstoff.id +
      "." +
      DownloadType[type].toLowerCase()
    );
  }

  reset() {
    this.reset_(true);
  }

  private reset_(withPump: boolean) {
    if (withPump) {
      (<any>this).pumpentyp = undefined;
    }
    this.downloadData.clear();
    this.state.productfinderDownloadData = this.downloadData;
    this.matrixData = new MatrixData();
  }

  get note(): string {
    let key = "productfinder.notes.downloads";

    let note = this.i18n.tr(key);

    if (note === key) return "";

    return note;
  }

  @computedFrom(
    "downloadData.pumpentyp",
    "downloadData.drehrichtung",
    "downloadData.befestigungsart",
    "downloadData.fluessigkeitsvorlage",
    "downloadData.gehaeuseanschluss",
    "downloadData.ventilanbau",
    "downloadData.gehaeusewerkstoff",
  )
  get valid(): boolean {
    return !!(
      this.downloadData.pumpentyp &&
      this.downloadData.drehrichtung &&
      this.downloadData.befestigungsart &&
      this.downloadData.fluessigkeitsvorlage &&
      this.downloadData.gehaeuseanschluss &&
      this.downloadData.ventilanbau &&
      this.downloadData.gehaeusewerkstoff
    );
  }
}
